import React, { useState, useEffect, useMemo } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { Divider } from 'primereact/divider';
import { Checkbox } from 'primereact/checkbox';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { AutoComplete } from 'primereact/autocomplete';
import { Tooltip } from 'primereact/tooltip';
import { validateDateRange } from '../../utils/dateUtils';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * GenericTableFilter - Filtreleme işlevleri için yeniden kullanılabilir bileşen
 *
 * @param {Object} props - Bileşen özellikleri
 * @param {Array} props.filterFields - Filtre alanlarının tanımlandığı dizi (manuel yöntem)
 * @param {Array} props.columns - Tablo kolonlarını içeren dizi (otomatik yöntem)
 * @param {Object} props.initialFilters - Başlangıç filtre değerleri
 * @param {Function} props.onSearch - Arama yapıldığında tetiklenecek fonksiyon
 * @param {Function} props.onFilterChange - Filtreler değiştiğinde tetiklenecek fonksiyon (kullanılmıyor)
 * @param {Function} props.onApplyFilters - Filtreler uygulandığında tetiklenecek fonksiyon
 * @param {Function} props.onResetFilters - Sıfırla butonuna tıklandığında tetiklenecek fonksiyon
 * @param {Object} props.customFilterTypes - Belirli alanlar için özel filtre tipleri
 * @param {Object} props.customFilterOptions - Belirli alanlar için özel seçenek listeleri
 * @param {Object} props.initiallyOpen - Başlangıçta filtre alanının açık mı kapalı mı geleceğini belirler
 * @returns {JSX.Element}
 */
const GenericTableFilter = ({
    filterFields = [],
    columns = [],
    initialFilters = {},
    onSearch,
    // eslint-disable-next-line no-unused-vars
    _onFilterChange = () => {},
    onApplyFilters,
    onResetFilters,
    customFilterTypes = {},
    customFilterOptions = {},
    initiallyOpen = false,
    hideLabels = false,
    textboxWidth = '300px'
}) => {
    const { th } = useLanguage();

    const [filterOpen, setFilterOpen] = useState(initiallyOpen);
    const [filters, setFilters] = useState(initialFilters);
    const [autoCompleteFiltered, setAutoCompleteFiltered] = useState({});
    const [dateValidationErrors, setDateValidationErrors] = useState({});

    // Varsayılan filtre alanları - columns ve filterFields yoksa kullanılacak
    const defaultFilterFields = useMemo(
        () => [
            {
                field: 'markaAdi',
                headerName: th('filter.brand'),
                type: 'text'
            },
            {
                field: 'modelAdi',
                headerName: th('filter.modelName'),
                type: 'text'
            },
            {
                field: 'modelKodu',
                headerName: th('filter.modelCode'),
                type: 'text'
            }
        ],
        [th]
    );

    // Kolonlardan filtre alanlarını otomatik olarak oluştur
    const generatedFilterFields = useMemo(() => {
        // Manuel filterFields tanımlanmışsa, onları kullan
        if (filterFields && filterFields.length > 0) {
            return filterFields;
        }

        // PrimeReact Column bileşenlerinden filtre alanlarını oluştur
        if (columns && columns.length > 0) {
            return columns
                .filter((col) => col.props?.field && col.props?.header) // Sadece field ve header'ı olan kolonları dahil et
                .filter((col) => col.props.field !== 'id' && !col.props.body) // ID alanını ve özel body'si olan kolonları hariç tut
                .map((col) => {
                    const field = col.props.field;
                    const headerName = col.props.header;
                    const filterPlaceholder = col.props.filterPlaceholder;

                    // Özel filtre tipi tanımlanmışsa onu kullan
                    if (customFilterTypes[field]) {
                        return {
                            field,
                            headerName,
                            type: customFilterTypes[field],
                            filterPlaceholder,
                            options: customFilterOptions[field] || []
                        };
                    }

                    // Veri tipine göre uygun filtre türünü belirle
                    let type = 'text';
                    if (col.props.dataType === 'numeric') {
                        type = 'number';
                    } else if (col.props.dataType === 'date') {
                        type = 'date';
                    } else if (col.props.dataType === 'boolean' || field === 'aktif' || field.toLowerCase().includes('aktif')) {
                        type = 'boolean';
                    }

                    return {
                        field,
                        headerName,
                        type,
                        filterPlaceholder,
                        options: customFilterOptions[field] || []
                    };
                });
        }

        // Varsayılan filtre alanlarını döndür
        return defaultFilterFields;
    }, [filterFields, columns, customFilterTypes, customFilterOptions, defaultFilterFields]);

    // Initialize autoComplete options when customFilterOptions change
    useEffect(() => {
        // Iterate through customFilterOptions and set initial values for autocomplete fields
        Object.entries(customFilterOptions).forEach(([field, options]) => {
            if (options && options.length > 0 && (customFilterTypes[field] === 'autoComplete' || (generatedFilterFields && generatedFilterFields.some((f) => f.field === field && f.type === 'autoComplete')))) {
                setAutoCompleteFiltered((prev) => ({
                    ...prev,
                    [field]: [...options]
                }));
            }
        });
    }, [customFilterOptions, customFilterTypes, generatedFilterFields]);

    // Filtre panelini açıp kapatır
    const handleFilterToggle = () => {
        setFilterOpen((prev) => !prev);
    };

    // Herhangi bir filtre değeri değiştiğinde çağrılır (sadece yerel state'i günceller)
    const handleFilterChange = (field, value) => {
        const updatedFilters = { ...filters, [field]: value };
        setFilters(updatedFilters);

        // Tarih alanları için validasyon kontrolü
        if (field.toLowerCase().includes('startdate') || field.toLowerCase().includes('enddate') || field.toLowerCase().includes('baslangic') || field.toLowerCase().includes('bitis')) {
            validateDateFields(field, value, updatedFilters);
        }

        // Filtreleme sadece Uygula butonuna basıldığında gerçekleşecek
    };

    // Tarih alanları için validasyon kontrolü
    const validateDateFields = (changedField, changedValue, currentFilters) => {
        let startDateField = null;
        let endDateField = null;
        let startDateValue = null;
        let endDateValue = null;

        // Başlangıç ve bitiş tarihlerini bul
        Object.keys(currentFilters).forEach((key) => {
            const keyLower = key.toLowerCase();
            if (keyLower.includes('startdate') || keyLower.includes('baslangic')) {
                startDateField = key;
                startDateValue = key === changedField ? changedValue : currentFilters[key];
            } else if (keyLower.includes('enddate') || keyLower.includes('bitis')) {
                endDateField = key;
                endDateValue = key === changedField ? changedValue : currentFilters[key];
            }
        });

        // Her iki tarih de varsa validasyon yap
        if (startDateField && endDateField && startDateValue && endDateValue) {
            const validation = validateDateRange(startDateValue, endDateValue);

            // Daha kısa hata mesajı kullan
            const shortErrorMessage = validation.isValid ? null : th('filter.dateRangeError');

            setDateValidationErrors((prev) => ({
                ...prev,
                [startDateField]: shortErrorMessage,
                [endDateField]: shortErrorMessage
            }));
        } else {
            // Hataları temizle
            setDateValidationErrors((prev) => ({
                ...prev,
                [startDateField]: null,
                [endDateField]: null
            }));
        }
    };

    // Filtreleri sıfırlar
    const handleResetFilters = () => {
        // Başlangıç filtrelerini kullan, eğer varsa
        const defaultFilters = { ...initialFilters };
        setFilters(defaultFilters);
        setDateValidationErrors({});

        // Dış bileşene sıfırlama bilgisini ilet (eğer tanımlanmışsa)
        if (onResetFilters) {
            onResetFilters();
        }
    };

    // Filtreleri uygular
    const handleApplyFilters = () => {
        // Tarih validasyon hatası varsa işlemi durdur
        const hasDateError = Object.values(dateValidationErrors).some((error) => error !== null);
        if (hasDateError) {
            return;
        }

        if (onApplyFilters) {
            onApplyFilters(filters);
        }
    };

    // Enter tuşu ile filtreleri uygular
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleApplyFilters();
        }
    };

    // AutoComplete için öğeleri filtreler - gelişmiş arama ile
    const filterAutoComplete = (event, field, options) => {
        const query = event.query ? event.query.toLowerCase() : '';
        let filtered;

        // First get options from customFilterOptions if they're not passed directly or are empty
        const effectiveOptions = options && options.length > 0 ? options : customFilterOptions[field] || [];

        if (effectiveOptions.length > 0) {
            // Eğer arama metni boşsa tüm seçenekleri göster, değilse filtreleme yap
            if (!query || query.trim() === '') {
                filtered = [...effectiveOptions]; // Create a new array to ensure state update
            } else {
                // Gelişmiş filtreleme - arama metni herhangi bir yerde geçiyorsa sonuçlara dahil et
                filtered = effectiveOptions.filter((option) => {
                    const label = option.label ? option.label.toLowerCase() : '';
                    const value = option.value ? String(option.value).toLowerCase() : '';
                    return label.includes(query) || value.includes(query);
                });
            }
        } else {
            // Eğer hiç seçenek yoksa boş dizi döndür
            filtered = [];
        }

        // State'i güncelle
        setAutoCompleteFiltered((prev) => ({
            ...prev,
            [field]: filtered
        }));
    };

    // AutoComplete dropdown'u açıldığında tüm seçenekleri göster
    const handleAutoCompleteShow = (event, field, options) => {
        // Get options from customFilterOptions if they're not passed directly or are empty
        const effectiveOptions = options && options.length > 0 ? options : customFilterOptions[field] || [];

        // Tüm seçenekleri göster
        setAutoCompleteFiltered((prev) => ({
            ...prev,
            [field]: effectiveOptions.length > 0 ? [...effectiveOptions] : []
        }));
    };

    const renderFilterField = (field, key) => {
        const { field: fieldName, headerName, type = 'text', options = [] ,filterPlaceholder} = field;
        const value = filters[fieldName] !== undefined ? filters[fieldName] : '';
        const effectiveOptions = options && options.length > 0 ? options : customFilterOptions[fieldName] || [];

        switch (type) {
            case 'select':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        <Dropdown
                            id={fieldName}
                            value={value}
                            options={options}
                            onChange={(e) => handleFilterChange(fieldName, e.value)}
                            placeholder={filterPlaceholder || th('filter.selectPlaceholder', { field: headerName })}
                            showClear
                            className="w-full"
                        />
                    </div>
                );
            case 'multiselect':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        <MultiSelect
                            id={fieldName}
                            value={value || []}
                            options={effectiveOptions}
                            onChange={(e) => handleFilterChange(fieldName, e.value)}
                            placeholder={filterPlaceholder || headerName}
                            className="w-full"
                            filter
                            filterBy="label"
                            optionLabel="label"
                            optionValue="value"
                            display="chip"
                            showClear
                            maxSelectedLabels={2}
                            selectedItemsLabel={`{0} ${th('common.selected') || 'seçildi'}`}
                            emptyFilterMessage={th('filter.noResultsFound') || 'Sonuç bulunamadı'}
                            resetFilterOnHide
                            showSelectAll={false}
                            virtualScrollerOptions={{ itemSize: 38 }}
                        />
                    </div>
                );

            case 'dropdown':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        <Dropdown
                            id={fieldName}
                            value={value}
                            options={options}
                            onChange={(e) => handleFilterChange(fieldName, e.value)}
                            placeholder={th('filter.dropdownPlaceholder', { field: headerName })}
                            showClear
                            className="w-full"
                            filter
                            filterBy="label,value"
                            optionLabel="label"
                            optionValue="value"
                        />
                    </div>
                );

            case 'serverDropdown':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        <Dropdown
                            key={field.dropdownKey ?? fieldName}
                            id={fieldName}
                            value={value}
                            options={options}
                            onChange={(e) => {
                                handleFilterChange(fieldName, e.value);
                                if (field.onChange) {
                                    field.onChange(e.value);
                                }
                            }}
                            placeholder={field.placeholder || th('filter.dropdownPlaceholder', { field: headerName })}
                            showClear
                            className={`w-full ${field.noResults ? 'p-invalid' : ''}`}
                            filter
                            filterBy="label"
                            filterMatchMode="contains"
                            filterLocale={field.filterLocale}
                            filterValue={field.filterValue}
                            onFilter={(e) => field.onFilter?.(e)}
                            optionLabel="label"
                            optionValue="value"
                            loading={!!field.loading}
                            emptyFilterMessage={th('filter.noResultsFound')}
                        />
                    </div>
                );

            case 'custom':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        {field.customComponent &&
                            field.customComponent({
                                value,
                                onChange: (val) => handleFilterChange(fieldName, val)
                            })}
                    </div>
                );

            case 'autoComplete':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        <AutoComplete
                            id={fieldName}
                            value={value}
                            suggestions={autoCompleteFiltered[fieldName] || []}
                            completeMethod={(e) => filterAutoComplete(e, fieldName, options)}
                            onFocus={(e) => handleAutoCompleteShow(e, fieldName, options)}
                            onClick={(e) => handleAutoCompleteShow(e, fieldName, options)}
                            onShow={() => {
                                // Always ensure suggestions are populated on dropdown show
                                // Get options from customFilterOptions if direct options are empty
                                const effectiveOptions = options && options.length > 0 ? options : customFilterOptions[fieldName] || [];

                                setAutoCompleteFiltered((prev) => ({
                                    ...prev,
                                    [fieldName]: effectiveOptions.length > 0 ? [...effectiveOptions] : []
                                }));
                            }}
                            field="label"
                            forceSelection={false}
                            onChange={(e) => handleFilterChange(fieldName, e.value)}
                            placeholder={th('filter.autoCompletePlaceholder', { field: headerName })}
                            className="w-full"
                            dropdown
                            dropdownAutoFocus
                            dropdownMode="current"
                            minLength={0}
                            emptyMessage={th('filter.noOptionsFound')}
                            delay={200} // 200ms gecikme ile tetikleme
                        />
                    </div>
                );
            case 'boolean':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <div className="flex align-items-center mt-1">
                            <Checkbox inputId={fieldName} checked={value === true} onChange={(e) => handleFilterChange(fieldName, e.checked)} />
                            <label htmlFor={fieldName} className="ml-2 text-sm">
                                {headerName}
                            </label>
                        </div>
                    </div>
                );

            case 'number':
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                        </label>
                        <InputNumber id={fieldName} value={value} onValueChange={(e) => handleFilterChange(fieldName, e.value)} placeholder={th('filter.inputPlaceholder', { field: headerName })} className="w-full" />
                    </div>
                );

            case 'date': {
                const tooltipId = `date-error-${fieldName}`;
                return (
                    <div className="field mb-2 p-1" key={key}>
                        <label htmlFor={fieldName} className="block mb-1 text-sm">
                            {headerName}
                            {dateValidationErrors[fieldName] && (
                                <>
                                    <i id={tooltipId} className="pi pi-exclamation-triangle ml-2 text-red-500" style={{ fontSize: '0.75rem', cursor: 'help' }} />
                                    <Tooltip target={`#${tooltipId}`} content={dateValidationErrors[fieldName]} position="top" />
                                </>
                            )}
                        </label>
                        <Calendar
                            id={fieldName}
                            value={value}
                            onChange={(e) => handleFilterChange(fieldName, e.value)}
                            dateFormat="dd/mm/yy"
                            showIcon
                            placeholder={th('filter.datePlaceholder', { field: headerName })}
                            className={`w-full ${dateValidationErrors[fieldName] ? 'p-invalid' : ''}`}
                        />
                    </div>
                );
            }

            case 'text':
            default:
                return (
                    <div className="field mb-2 p-1" key={key}>
                        {!hideLabels && (
                            <label htmlFor={fieldName} className="block mb-1 text-sm">
                                {headerName}
                            </label>
                        )}
                        <InputText
                            id={fieldName}
                            value={value}
                            onChange={(e) => handleFilterChange(fieldName, e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={filterPlaceholder || headerName || th('filter.inputPlaceholder', { field: headerName })}
                            className="w-full"
                        />
                    </div>
                );
        }
    };

    return (
        <div className="generic-table-filter">
            <div className="grid">
                <div className="col-12 md:col-6 flex justify-content-start">
                    <Button 
                        icon="pi pi-filter" 
                        label={th('filter.advancedFilter')} 
                        onClick={handleFilterToggle} 
                        outlined={!filterOpen} 
                        className={filterOpen ? '' : ''}
                        style={{ minWidth: '120px' }}
                    />
                </div>
            </div>

            {filterOpen && (
                <div className="mt-3">
                    <Divider />
                    <div className="text-xl font-medium mb-3">{th('filter.advancedFilters')}</div>
                    {generatedFilterFields.length > 0 ? (
                        <div className="grid">
                            {generatedFilterFields.map((field, index) => (
                                <div key={index} className="col-12 md:col-6 lg:col-3">
                                    {renderFilterField(field, index)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex justify-content-center align-items-center p-4">
                            <span className="text-500">{th('filter.filterFieldsError')}</span>
                        </div>
                    )}

                    <div className="flex justify-content-start mt-3">
                        <Button label={th('filter.reset')} icon="pi pi-refresh" className="p-button-outlined mr-2" onClick={handleResetFilters} />
                        <Button label={th('filter.apply')} icon="pi pi-check" onClick={handleApplyFilters} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default GenericTableFilter;